/**
 * 权限状态管理
 * 负责动态路由生成、菜单管理、权限检查
 *
 * @author system
 * @version 2.0.0 - 完全动态路由版本
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { getCurrentUserMenu } from '@/api/menu'
import { RouteBuilder } from '@/router/RouteBuilder'
import { useUserStore } from '@/stores/user'

/**
 * 缓存键名
 */
const CACHE_KEY_MENU_TREE = 'cached_menu_tree'
const CACHE_KEY_VERSION = 'menu_version'

export const usePermissionStore = defineStore('permission', () => {
  // ============ 状态 ============

  /** 当前用户的路由列表 */
  const routes = ref([])

  /** 兼容旧代码的addRoutes */
  const addRoutes = ref([])

  /** 菜单树（用于侧边栏显示） */
  const menuTree = ref([])

  /** 用户权限列表 */
  const permissions = ref([])

  /** 路由是否已生成 */
  const isRoutesGenerated = ref(false)

  /** 菜单版本号（用于检测更新） */
  const menuVersion = ref(0)

  /** 路由加载中状态 */
  const isLoading = ref(false)

  // ============ 计算属性 ============

  /** 是否有超级管理员权限 */
  const isSuperAdmin = computed(() => {
    return permissions.value.includes('*:*:*')
  })

  // ============ 核心方法 ============

  /**
   * 生成用户路由
   * 从后端获取菜单树，转换为Vue Router路由配置
   *
   * @param {Array} userPermissions - 用户权限列表（兼容旧API）
   * @returns {Promise<Array>} 生成的路由配置数组
   */
  async function generateRoutes(userPermissions) {
    if (isLoading.value) {
      return routes.value
    }

    isLoading.value = true

    try {
      const userStore = useUserStore()
      const token = userStore.token

      // 保存用户权限
      if (userPermissions) {
        permissions.value = userPermissions
      } else {
        permissions.value = userStore.permissions || []
      }

      // 检查token是否存在（getCurrentUserMenu基于token获取菜单）
      if (!token) {
        // 尝试从缓存恢复
        const cachedData = loadFromCache()
        if (cachedData) {
          routes.value = cachedData.routes
          addRoutes.value = cachedData.routes
          menuTree.value = cachedData.menuTree
          isRoutesGenerated.value = true
          return cachedData.routes
        }
        throw new Error('Token不存在，无法获取菜单')
      }

      // 1. 从后端获取当前用户菜单树（已按权限过滤），带重试
      const response = await fetchMenuWithRetry()
      const { data } = response

      // 2. 保存菜单树（供侧边栏使用）
      menuTree.value = data || []

      // 3. 使用RouteBuilder转换为路由配置
      const routeBuilder = new RouteBuilder()
      const dynamicRoutes = routeBuilder.buildRoutes(data)

      // 4. 根据权限过滤（如果后端未过滤）
      let accessedRoutes = dynamicRoutes

      if (!isSuperAdmin.value && permissions.value.length > 0) {
        accessedRoutes = filterAsyncRoutes(dynamicRoutes, permissions.value)
      }

      // 5. 保存路由列表
      routes.value = accessedRoutes
      addRoutes.value = accessedRoutes
      isRoutesGenerated.value = true

      // 6. 更新版本号
      menuVersion.value++

      // 7. 缓存到localStorage
      saveToCache(accessedRoutes, data)

      return accessedRoutes

    } catch (error) {
      console.error('生成路由失败:', error)

      // 尝试从缓存恢复
      const cachedData = loadFromCache()
      if (cachedData) {
        routes.value = cachedData.routes
        addRoutes.value = cachedData.routes
        menuTree.value = cachedData.menuTree
        isRoutesGenerated.value = true
        return cachedData.routes
      }

      // 缓存也没有，设置空状态
      routes.value = []
      addRoutes.value = []
      menuTree.value = []
      isRoutesGenerated.value = true

      return []

    } finally {
      isLoading.value = false
    }
  }

  /**
   * 重新加载路由（菜单更新后调用）
   * 移除旧路由，重新生成并注册新路由
   *
   * @param {Object} router - Vue Router实例
   */
  async function reloadRoutes(router) {
    try {
      // 1. 移除旧的动态路由
      routes.value.forEach(route => {
        if (route.name && router.hasRoute(route.name)) {
          router.removeRoute(route.name)
        }
      })

      // 2. 重置状态
      isRoutesGenerated.value = false
      routes.value = []
      addRoutes.value = []
      menuTree.value = []

      // 3. 重新生成路由
      const newRoutes = await generateRoutes(permissions.value)

      // 4. 添加新路由
      newRoutes.forEach(route => {
        router.addRoute(route)
      })

      // 5. 检查当前页面是否仍然存在
      const currentRoute = router.currentRoute.value
      if (currentRoute.name && !router.hasRoute(currentRoute.name)) {
        router.push('/dashboard')
      }

      return newRoutes

    } catch (error) {
      console.error('重载路由失败:', error)
      throw error
    }
  }

  /**
   * 根据权限过滤路由
   */
  function filterAsyncRoutes(routeList, perms) {
    const res = []

    routeList.forEach(route => {
      const tmp = { ...route }
      const allowed = hasPermission(perms, tmp)

      if (allowed) {
        if (tmp.children) {
          tmp.children = filterAsyncRoutes(tmp.children, perms)
        }
        res.push(tmp)
      }
    })

    return res
  }

  /**
   * 判断是否有权限
   */
  function hasPermission(perms, route) {
    // 如果用户有超级管理员权限，直接返回true
    if (perms.includes('*:*:*')) {
      return true
    }

    // 如果路由没有权限要求，直接返回true
    // 注意：空数组也视为无权限要求
    const routePerms = route.meta?.perms
    const routeRoles = route.meta?.roles
    const hasPermsRequirement = routePerms && routePerms.length > 0
    const hasRolesRequirement = routeRoles && routeRoles.length > 0

    if (!route.meta || (!hasPermsRequirement && !hasRolesRequirement)) {
      return true
    }

    // 检查权限字符串匹配
    if (hasPermsRequirement) {
      if (Array.isArray(routePerms)) {
        return routePerms.some(p => perms.includes(p))
      }
      return perms.includes(routePerms)
    }

    // 检查角色匹配（兼容旧版本）
    if (hasRolesRequirement) {
      return perms.some(role => routeRoles.includes(role))
    }

    return true
  }

  /**
   * 重置权限状态
   * 用于退出登录时清理状态
   */
  function resetPermission() {
    routes.value = []
    addRoutes.value = []
    menuTree.value = []
    permissions.value = []
    isRoutesGenerated.value = false
    menuVersion.value = 0
    isLoading.value = false

    // 清除缓存
    clearCache()
  }

  /**
   * 检查是否有特定按钮权限
   *
   * @param {string} permission - 权限标识（如: system:user:add）
   * @returns {boolean} 是否有权限
   */
  function hasBtnPermission(permission) {
    // 超级管理员拥有所有权限
    if (isSuperAdmin.value) {
      return true
    }
    return permissions.value.includes(permission)
  }

  /**
   * 检查是否有多个权限中的任意一个
   *
   * @param {Array<string>} permissionList - 权限标识数组
   * @returns {boolean} 是否有任意一个权限
   */
  function hasAnyPermission(permissionList) {
    if (!permissionList || permissionList.length === 0) {
      return true
    }
    if (isSuperAdmin.value) {
      return true
    }
    return permissionList.some(p => permissions.value.includes(p))
  }

  /**
   * 检查是否有所有权限
   *
   * @param {Array<string>} permissionList - 权限标识数组
   * @returns {boolean} 是否有所有权限
   */
  function hasAllPermissions(permissionList) {
    if (!permissionList || permissionList.length === 0) {
      return true
    }
    if (isSuperAdmin.value) {
      return true
    }
    return permissionList.every(p => permissions.value.includes(p))
  }

  // ============ 带重试的菜单请求 ============

  /**
   * 获取菜单数据，超时自动重试（最多3次）
   */
  async function fetchMenuWithRetry(maxRetries = 3) {
    let lastError = null
    for (let i = 0; i < maxRetries; i++) {
      try {
        if (i > 0) {
          // 重试前等待一段时间
          await new Promise(resolve => setTimeout(resolve, 1000 * i))
        }
        const response = await getCurrentUserMenu()
        return response
      } catch (error) {
        lastError = error
        const isTimeout = error.code === 'ECONNABORTED' || (error.message && error.message.includes('timeout'))
        const isNetworkError = error.message && error.message.includes('Network Error')
        if (isTimeout || isNetworkError) {
          continue
        }
        // 非超时/网络错误，直接抛出
        throw error
      }
    }
    throw lastError
  }

  // ============ 缓存相关方法 ============

  /**
   * 保存到缓存
   */
  function saveToCache(routeData, menuData) {
    try {
      // 菜单树可以序列化
      localStorage.setItem(CACHE_KEY_MENU_TREE, JSON.stringify(menuData))
      localStorage.setItem(CACHE_KEY_VERSION, String(menuVersion.value))
    } catch (e) {
      console.warn('缓存路由失败:', e)
    }
  }

  /**
   * 从缓存加载
   */
  function loadFromCache() {
    try {
      const menuData = localStorage.getItem(CACHE_KEY_MENU_TREE)
      if (!menuData) {
        return null
      }

      const parsedMenuTree = JSON.parse(menuData)

      // 重新构建路由
      const routeBuilder = new RouteBuilder()
      const cachedRoutes = routeBuilder.buildRoutes(parsedMenuTree)

      return {
        routes: cachedRoutes,
        menuTree: parsedMenuTree
      }
    } catch (e) {
      console.warn('读取缓存失败:', e)
      return null
    }
  }

  /**
   * 清除缓存
   */
  function clearCache() {
    try {
      localStorage.removeItem(CACHE_KEY_MENU_TREE)
      localStorage.removeItem(CACHE_KEY_VERSION)
    } catch (e) {
      console.warn('清除缓存失败:', e)
    }
  }

  /**
   * 获取缓存的菜单版本号
   */
  function getCachedVersion() {
    try {
      return parseInt(localStorage.getItem(CACHE_KEY_VERSION) || '0', 10)
    } catch (e) {
      return 0
    }
  }

  // ============ 导出 ============

  return {
    // 状态
    routes,
    addRoutes,
    menuTree,
    permissions,
    isRoutesGenerated,
    menuVersion,
    isLoading,

    // 计算属性
    isSuperAdmin,

    // 方法
    generateRoutes,
    reloadRoutes,
    resetPermission,
    hasBtnPermission,
    hasAnyPermission,
    hasAllPermissions,
    getCachedVersion
  }
})
